import { attr, Model, many } from 'redux-orm';
import { AppAction, store } from 'src';
import { IORMModels, ISession } from 'src/Config/ConfigureOrm';
import {
  WEB_GET_ACTIVITY_THEMES_SUCCEEDED,
  WEB_GET_ACTIVITY_THEMES_FAILED
} from './ActivityTheme.Actions';
import { IActivityModel } from '../Activity/Activity';
import { IActivityCategoryModel } from '../ActivityCategory/ActivityCategory';

import arbejds from '../../images/Courses/arbejdsomraadekurser.jpg';
import almene from '../../images/Courses/almenekurser.jpg';
import jofunc from '../../images/Courses/jobfunktionkurser.jpg';
import { ITagProps } from 'src/components/Tag';

import almeneCoverImage from '../../images/Courses/AlmeneAndre.jpg';
import jobfunktionCoverImage from '../../images/Courses/JobfunktionAndre.jpg';
import arbejdsomraadeCoverImage from '../../images/Courses/ArbejdsomraadeAndre.jpg';
import activityStatus from '../../utils/activityStatusUtils';

export interface IActivityThemeModel {
  themeId: number;
  name: string;
  description: string;
  themeImg: string;
  coverImage: string | undefined;
  buttonText: string | null;
  themeImageUrl: string | null;
  categories: IActivityCategoryModel[];
  // Local use
  NumberOfCoursesInTheme: number;
}

function getThemeImage(theme: IActivityThemeModel) {
  const { name } = theme;

  if (name.indexOf('Almene') !== -1) {
    return almene;
  }
  if (name.indexOf('Job') !== -1) {
    return jofunc;
  }
  if (name.indexOf('Arbejds') !== -1) {
    return arbejds;
  }
}

const coverImagesByThemeName: object = {
  'Almene tværfaglige': almeneCoverImage,
  Jobfunktion: jobfunktionCoverImage,
  Arbejdsområde: arbejdsomraadeCoverImage
};

function addCoverImageToTheme(theme: IActivityThemeModel): string | undefined {
  return coverImagesByThemeName[theme.name];
}

export default class ActivityTheme extends Model<IActivityThemeModel>
  implements IActivityThemeModel {
  public static modelName = 'ActivityTheme';

  public static fields = {
    themeId: attr({}),
    title: attr({}),
    categories: many('ActivityCategory', 'theme')
  };
  public static setActivityTag(act: IActivityModel): ITagProps | undefined {
    const actStatus = activityStatus(act);

    if (actStatus === 'isAdvertisement') {
      return {
        color: 'info',
        children: 'KURSUS PÅ VEJ',
        tooltip:
          'Kurset oprettes efter behov. Tilmeld dig for at vise din interesse. Du vil blive kontaktet af udbyder ved oprettelse af et kursus med en konkret dato.'
      };
    } else if (actStatus === 'isCancelled') {
      return {
        color: 'danger',
        children: 'AFLYST',
        tooltip:
          'Kurset er aflyst. Hvis du er interesseret i et lignende kursus, anbefales du at kontakte udbyder eller at søge på Kursusportalen'
      };
    } else if (actStatus === 'isFocus') {
      return {
        color: 'warning',
        children: 'FOKUS',
        tooltip:
          'Kurset er enten nyt på Kursusportalen, der er ledige pladser eller andre ting du skal være opmærksom på. Kontakt evt. udbyder.'
      };
    } else if (actStatus === 'lastEnrollmentDatePassed') {
      return {
        color: 'cancelled',
        children: 'TILMELDINGSFRIST UDLØBET',
        tooltip:
          'Hvis du er interesseret i et lignende kursus, anbefales du at kontakte udbyder'
      };
    } else if (actStatus === 'lastChance') {
      return {
        color: 'warning',
        children: 'SIDSTE CHANCE',
        tooltip: 'Der er mindre end 4 dage til tilmeldingsfristen udløber'
      };
    } else if (actStatus === 'success') {
      return {
        color: 'success',
        children: 'NYT',
        tooltip:
          'Dette kursus er udbudt på Kursusportalen inden for de sidste 14 dage'
      };
    }
    return undefined;
  }
  public static reducer(
    action: AppAction<any>,
    activityTheme: typeof Model,
    session: ISession<IORMModels>
  ) {
    // const fillerText = "FILLER - Eu aliquip incididunt excepteur irure sit. Magna aqua veniam cillum labore enim pariatur minim lare lore. Aliquip est voluptate labore nostrud ipsum. -- REMOVE ME!"
    switch (action.type) {
      case WEB_GET_ACTIVITY_THEMES_SUCCEEDED:
        const themes: IActivityThemeModel[] = action.payload;
        themes.forEach(actTheme => {
          actTheme.NumberOfCoursesInTheme = 0;
          const actThemeToAdd = {
            name: actTheme.name,
            themeId: actTheme.themeId,
            buttonText: actTheme.buttonText,
            themeImageUrl: actTheme.themeImageUrl,
            description: actTheme.description,
            themeImg: getThemeImage(actTheme),
            NumberOfCoursesInTheme: 0,
            coverImage: addCoverImageToTheme(actTheme)
          };
          let actCount = 0;
          activityTheme.upsert<IActivityThemeModel>(actThemeToAdd);
          actTheme.categories.forEach(cat => {
            const categoryToAdd = Object.assign(
              {},
              {
                categoryId: cat.categoryId,
                name: cat.name,
                themeId: actThemeToAdd.themeId
              }
            );
            session.ActivityCategory.upsert<IActivityCategoryModel>(
              categoryToAdd
            );
            activityTheme
              .withId(actThemeToAdd.themeId.toString())
              .categories.add(categoryToAdd);
            actCount += cat.activities.length;
            cat.activities.forEach(act => {
              const actToAdd = Object.assign(
                {},
                {
                  detailKursusId: act.detailKursusId,
                  admStatus: act.admStatus,
                  courseTitle: act.courseTitle,
                  fiuNo: act.fiuNo,
                  teaserText: act.teaserText,
                  startDate: act.startDate,
                  endDate: act.endDate,
                  startTime: act.startTime,
                  endTime: act.endTime,
                  courseLocation: act.courseLocation,
                  isFocus: act.isFocus,
                  isAdvertisement: act.isAdvertisement,
                  signUpForm: act.signUpForm,
                  filterFromCourseCarousel: act.filterFromCourseCarousel,
                  isInternal: act.isInternal,
                  categoryId: cat.categoryId,
                  tag: this.setActivityTag(act),
                  lastEnrollmentDate: act.lastEnrollmentDate,
                  lastEnrollmentDateEndTime: act.lastEnrollmentDateEndTime,
                  description: act.description
                }
              );

              if (!session.Activity.hasId(actToAdd.detailKursusId.toString())) {
                session.Activity.upsert<IActivityModel>(actToAdd);
              }

              session.ActivityCategory.withId(
                cat.categoryId.toString()
              ).activities.add(actToAdd);
            });
            const actThemeCourseNo = {
              themeId: actTheme.themeId,
              NumberOfCoursesInTheme: actCount
            };

            activityTheme.upsert<IActivityThemeModel>(actThemeCourseNo);
          });
        });
        break;
      case WEB_GET_ACTIVITY_THEMES_FAILED:
        console.error('activityThemes failed');
        break;
    }
  }

  public themeId: number;
  public name: string;
  public description: string;
  public themeImg: string;
  public buttonText: string | null;
  public themeImageUrl: string | null;
  public coverImage: string;
  public categories: IActivityCategoryModel[];
  public NumberOfCoursesInTheme: number;
}



// WEBPACK FOOTER //
// ./src/Entities/ActivityTheme/ActivityTheme.ts